import DateRange from '../../core/dateRange'
import {SimpleBlueButton} from '../../core/button'
import text from './text.json'
import ChooseTime from '../monitoring/ChooseTime'
import './index.scss'
import {useState, useMemo, useCallback, useEffect} from 'react'
import {addHours, addDays} from 'date-fns'
import useInterval from 'use-interval'
import moment from 'moment'
import {setActiveTimeIndex, setRangeSelected} from '../../../stores/monitoring'
import {useDispatch, useSelector} from 'react-redux'
import {timesDataFactory} from '../monitoring/times'
import renderSkeletonLoaders from '../../../utils/loader'
import {formatToISOUTC, modifyUTCDate} from '../../../utils/date'

const SELECTION = 'selection'
const HEARTBEAT_MONITORING = 'heartbeat_monitoring'
const RANGE = 'range'
const DAY = 'day'
const WEEK_TIME_INDEX = 5
const INITIAL_TIME_INDEX = 0
const ON_DEMAND = 'on-demand'
const HOUR = 'hour'
const FILTER = 'filter'
const MAX_ON_DEMAND_VALUE = 3
const MAX_HOUR_VALUE = 10

const calculateHeartbeatInterval = (hoursDifference, daysDifference, data) => {
    const currentInterval = data.find(item =>
        item.key.includes('h')
            ? item.value === hoursDifference
            : item.value === daysDifference * 24
    )
    return currentInterval?.interval || RANGE
}

const calculateDefaultInterval = (hoursDifference, daysDifference) => {
    if (hoursDifference <= MAX_ON_DEMAND_VALUE) return ON_DEMAND
    if (hoursDifference > MAX_ON_DEMAND_VALUE && daysDifference <= MAX_HOUR_VALUE) return HOUR
    return DAY
}

const defaultTime = moment()

const DateInterval = ({
    callbackFn,
    resetFn,
    isAutoRefreshEnabled,
    setIsFiltering,
    type = null,
    isLoading = false
}) => {
    const currentDate = new Date()
    const dispatch = useDispatch()
    const {rangeSelected, activeTimeIndex} = useSelector((state) => state.monitoring)
    const [startTime, setStartTime] = useState(defaultTime)
    const [endTime, setEndTime] = useState(defaultTime)

    const memoizedTimesData = useMemo(() => timesDataFactory(type), [type])

    const INITIAL_FILTERS = useMemo(() => ({
        interval: memoizedTimesData?.[activeTimeIndex]?.interval,
        dates: [{startDate: currentDate, endDate: currentDate, key: SELECTION}],
        value: memoizedTimesData?.[activeTimeIndex]?.value,
        limit: memoizedTimesData?.[activeTimeIndex]?.limit || null
    }), [memoizedTimesData, currentDate])

    const [filters, setFilters] = useState(INITIAL_FILTERS)

    const getStartDate = useCallback(
        (interval, value) => (
            type === HEARTBEAT_MONITORING
                ? filters.dates[0].startDate
                : interval === DAY
                    ? addDays(currentDate, -value)
                    : addHours(currentDate, -value)
        ),
        [filters, currentDate, type]
    )

    const handleChangeFilters = useCallback((filterData) => {
        if (!filterData?.interval) {
            setFilters((prev) => ({
                ...prev,
                dates: filterData,
                actionType: SELECTION
            }))
            return
        }

        dispatch(setRangeSelected(false))

        const selectedTime = memoizedTimesData.find((time) => time.key === filterData.interval) || {}
        const newFilters = {
            interval: selectedTime.interval,
            dates: INITIAL_FILTERS.dates,
            value: selectedTime.value,
            limit: selectedTime.limit,
            actionType: FILTER
        }

        setFilters(newFilters)

        const startDate = getStartDate(newFilters.interval, newFilters.value)

        callbackFn({
            startDate: formatToISOUTC(startDate),
            endDate: formatToISOUTC(currentDate),
            interval: newFilters.interval,
            showLoader: true,
            limit: selectedTime.limit
        })
    }, [dispatch, memoizedTimesData, callbackFn, currentDate, getStartDate, type, INITIAL_FILTERS])

    const applyFilters = useCallback(() => {
        dispatch(setRangeSelected(true))
        dispatch(setActiveTimeIndex(type === HEARTBEAT_MONITORING ? WEEK_TIME_INDEX : INITIAL_TIME_INDEX))
        const {startDate, endDate} = filters.dates[0]
        const modifiedStartDate = formatToISOUTC(modifyUTCDate(startDate, startTime))
        const modifiedEndDate = formatToISOUTC(modifyUTCDate(endDate, endTime))

        const hoursDifference = moment(modifiedEndDate).diff(moment(modifiedStartDate), 'hours')
        const daysDifference = moment(modifiedEndDate).diff(moment(modifiedStartDate), 'days')

        callbackFn({
            startDate: modifiedStartDate,
            endDate: modifiedEndDate,
            interval: type === HEARTBEAT_MONITORING ?
                calculateHeartbeatInterval(hoursDifference, daysDifference, memoizedTimesData) :
                calculateDefaultInterval(hoursDifference, daysDifference),
            showLoader: true
        })
    }, [dispatch, filters, callbackFn, type, startTime, endTime, memoizedTimesData])

    const resetFilters = useCallback(() => {
        dispatch(setRangeSelected(false))
        setFilters(INITIAL_FILTERS)
        resetFn()
    }, [dispatch, resetFn, type, INITIAL_FILTERS])

    const runAutoRefresh = useCallback(() => {
        const {interval, value} = filters
        const startDate = getStartDate(interval, value)

        callbackFn({
            startDate: formatToISOUTC(startDate),
            endDate: formatToISOUTC(currentDate),
            interval,
            showLoader: false,
            limit: filters?.limit
        })
    }, [filters, getStartDate, currentDate, callbackFn])

    useInterval(runAutoRefresh, isAutoRefreshEnabled && !rangeSelected ? process.env.REACT_APP_SERVER_MONITORING_LIVE_UPDATE_TIME : null)

    useEffect(() => {
        dispatch(setActiveTimeIndex(type === HEARTBEAT_MONITORING ? WEEK_TIME_INDEX : INITIAL_TIME_INDEX))
    }, [dispatch, type])

    return (
        <>
            {isLoading ? renderSkeletonLoaders(1) : <ChooseTime setIsFiltering={setIsFiltering} filters={memoizedTimesData} onChange={handleChangeFilters} />}
            {isLoading ? renderSkeletonLoaders(1, 'input-skeleton-sm') : <DateRange
                placeholder={text.date_range}
                state={filters.dates}
                onChange={handleChangeFilters}
                hasTimePicker
                onChangeStartTime={setStartTime}
                onChangeEndTime={setEndTime}
                startTime={startTime}
                endTime={endTime}>
                <div>
                    <SimpleBlueButton outline onClick={resetFilters}>
                        {text.reset}
                    </SimpleBlueButton>
                    <SimpleBlueButton onClick={applyFilters}>
                        {text.apply}
                    </SimpleBlueButton>
                </div>
            </DateRange>}
        </>
    )
}

export default DateInterval
